import { marked } from "marked";
import DOMPurify from "dompurify";

/**
 * Strips markdown formatting and returns plain text
 * Useful for copy operations where plain text is preferred
 * @param markdown The markdown text to convert to plain text
 * @returns Plain text without markdown formatting
 */
export function markdownToPlainText(markdown: string): string {
  if (!markdown) {
    return "";
  }

  try {
    // Convert to HTML first, then extract text content
    const html = markdownToWordHtml(markdown);
    
    // Create a temporary DOM element to extract text content
    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = html;
    
    return tempDiv.textContent || tempDiv.innerText || "";
  } catch (error) {
    console.error("Error converting markdown to plain text:", error);
    // Fallback: return original markdown
    return markdown;
  }
}

/**
 * Converts markdown text to HTML optimized for Word document insertion
 * @param markdown The markdown text to convert
 * @returns HTML string compatible with Word document insertion
 */
export function markdownToWordHtml(markdown: string): string {
  if (!markdown) {
    return "";
  }

  try {
    // Set marked options for Word compatibility
    marked.setOptions({
      breaks: true, // Convert line breaks to <br>
      gfm: true, // Enable GitHub Flavored Markdown
    });

    // Convert markdown to HTML
    const rawHtml = marked(markdown) as string;

    // Sanitize the HTML to remove any potentially unsafe content
    // while preserving Word-compatible formatting
    const sanitizedHtml = DOMPurify.sanitize(rawHtml, {
      ALLOWED_TAGS: [
        'p', 'br', 'strong', 'em', 'b', 'i', 'u',
        'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
        'ul', 'ol', 'li',
        'blockquote',
        'pre', 'code',
        'a', 'span', 'div'
      ],
      ALLOWED_ATTR: ['href', 'title'],
      KEEP_CONTENT: true,
    });

    return sanitizedHtml;
  } catch (error) {
    console.error("Error converting markdown to HTML:", error);
    // Fallback: return the original markdown wrapped in a paragraph
    return `<p>${markdown}</p>`;
  }
}
